import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * Cart total in minor units from a resolved Horizon 4.x cart lines update, or undefined.
 * The standard cart carries the total as a decimal string (`cost.totalAmount.amount`), built by
 * CartLinesUpdateEvent.createCartFromAjaxResponse from the AJAX cart's `total_price / 100`, so
 * multiplying back by 100 recovers live's `total_price` exactly.
 * @param {{ cost?: { totalAmount?: { amount?: string } } } | null | undefined} cart
 * @returns {number | undefined}
 */
function totalFromStandardCart(cart) {
  const amount = Number(cart?.cost?.totalAmount?.amount);
  return Number.isFinite(amount) ? Math.round(amount * 100) : undefined;
}

/**
 * <powerlete-free-ship>
 *
 * Cart-aware free-shipping progress bar. The element is rendered server-side with
 * the correct initial state (see snippets/powerlete-free-ship.liquid). The <script>
 * tag that loads this module is only emitted in the announcement-bar context, where
 * the element is NOT inside the cart drawer's morphing section and so must recompute
 * itself when the cart changes.
 *
 * In the cart drawer / cart page the element needs no JS: Horizon's CartItemsComponent
 * re-renders the whole cart section (morphSection) on every cart lines update, which
 * re-runs the Liquid and produces fresh server-formatted money. But customElements
 * upgrades are global — once this module loads (because an announcement-bar instance
 * is on the page), it would upgrade a drawer/cart instance too if not guarded, running
 * a second, redundant recompute on top of the server morph with client-side money
 * formatting that isn't guaranteed to match a market's presentment format. Bail out
 * for those contexts so they stay server-morph-only, as designed.
 *
 * Recompute model: read the per-market threshold (minor units) + message templates
 * from data-attributes, read the live cart total from the resolved cart lines update
 * (falling back to a /cart.js fetch), and rebuild the message + fill width. The
 * threshold string is pre-formatted server-side (market currency, possibly mislabeled
 * GBP in the metaobject) and reused verbatim; the live remainder is formatted with
 * Shopify's money format so it matches the presentment currency.
 */
class PowerleteFreeShip extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ctx === 'drawer' || this.dataset.ctx === 'cart') return;

    this.messageEl = this.querySelector('[data-free-ship-message]');
    this.fillEl = this.querySelector('[data-free-ship-fill]');

    this.threshold = parseInt(this.dataset.threshold || '0', 10);
    this.thresholdExpress = parseInt(this.dataset.thresholdExpress || '0', 10);
    this.thresholdStr = this.dataset.thresholdStr || '';
    this.currency = this.dataset.currency || 'GBP';
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    // Powerlete patch (audit I18N-03): last-resort fallback if the dataset attribute is
    // somehow missing entirely -- the snippet always passes one via powerlete.free_ship.*
    // (translated) or the merchant setting, so this only ever fires as English, but its
    // wording is aligned to match rather than drifting ("delivery" not "shipping", "only").
    this.freeText = this.dataset.freeText || 'Free delivery over [amount]';
    this.progressText = this.dataset.progressText || "You're only [amount] away from free delivery!";
    this.progressExpressText = this.dataset.progressExpressText || "You're only [amount] away from free express delivery!";
    this.successText = this.dataset.successText || 'Free delivery unlocked!';

    this.onCartUpdate = this.onCartUpdate.bind(this);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  /**
   * Horizon 4.x `shopify:cart:lines-update`: dispatched before the request, so wait for its
   * promise (the server's answer, when live's cart:update fired). A failed or superseded change
   * rejects and changes nothing, as live (which dispatched nothing on failure).
   * @param {Event & { promise?: Promise<{ cart?: any }> }} event
   */
  async onCartUpdate(event) {
    let result;
    try {
      result = await event?.promise;
    } catch (e) {
      return;
    }

    let total = totalFromStandardCart(result?.cart);
    if (typeof total !== 'number') {
      // Fallback: pull a fresh cart total if the event did not carry one.
      try {
        const res = await fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (res.ok) total = (await res.json()).total_price;
      } catch (e) {
        return;
      }
    }
    if (typeof total !== 'number') return;
    this.render(total);
  }

  /**
   * @param {number} total - cart total in minor units (presentment currency)
   */
  render(total) {
    if (!this.threshold || this.threshold <= 0) return;

    const hasExpress = this.thresholdExpress > this.threshold;
    const unlocked = total >= this.threshold;

    // Progress: vs standard while under it; vs express once standard is met (live behaviour).
    let pct;
    if (unlocked && hasExpress) {
      pct = Math.min(100, Math.round((total / this.thresholdExpress) * 100));
    } else {
      pct = Math.min(100, Math.round((total / this.threshold) * 100));
    }

    let msg;
    if (total <= 0) {
      msg = this.freeText.replace('[amount]', this.thresholdStr);
    } else if (unlocked) {
      if (hasExpress && total < this.thresholdExpress) {
        const remainingExp = Math.max(0, this.thresholdExpress - total);
        msg = this.progressExpressText.replace('[amount]', formatMoney(remainingExp, this.moneyFormat, this.currency));
      } else {
        msg = this.successText;
      }
    } else {
      const remainingStd = Math.max(0, this.threshold - total);
      msg = this.progressText.replace('[amount]', formatMoney(remainingStd, this.moneyFormat, this.currency));
    }

    if (this.messageEl) this.messageEl.textContent = msg;
    if (this.fillEl) this.fillEl.style.width = `${pct}%`;
    this.classList.toggle('is-unlocked', unlocked);
  }
}

if (!customElements.get('powerlete-free-ship')) {
  customElements.define('powerlete-free-ship', PowerleteFreeShip);
}
